import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';

interface GenericResponse<T> {
  payload?: T;
  error?: {
    status: number;
    message: string;
  };
}

export interface ProviderConfigDto {
  EntityID: string;
  Status: number;
  RevenueShare: number;
  CreationTime: string;
  LastUpdate: string;
  EditedByUserID: string | null;
}

export interface ConsumerConfigDto {
  EntityID: string;
  Status: number;
  PlanIncludedAudiences: number;
  PlanIncludedDrafts: number;
  PricePerAudience: number;
  PricePerDraft: number;
  CreationTime: string;
  LastUpdate: string;
  EditedByUserID: string | null;
}

export interface ProviderReportDto {
  EntityID: string;
  EntryID: string | null;
  ReportDate: string;
  ReportMonth: number;
  ReportYear: number;
  EntryType: 'audience' | 'custom';
  EntryDescription: string | null;
  EntryAmount: number;
  EntryCost: number;
}

export interface UpdateProviderReportPayload {
  EntryID: string;
  ReportDate?: string;
  EntryDescription?: string;
  Earnings?: number;
}

export interface ConsumerReportDto {
  EntityID: string;
  EntryID: string | null;
  ReportMonth: string;
  LastRecordDate: string | null;
  EntryType: 'audience' | 'draft' | 'custom';
  EntryDescription: string | null;
  PlanIncludedCount: number;
  PlanUnitaryCost: number;
  TotalAmount: number;
  TotalCost: number;
  EntryList: string[];
}

export interface UpdateConsumerReportPayload {
  EntryID: string;
  ReportDate?: string;
  EntryDescription?: string;
  EntryAmount?: number;
  EntryCost?: number;
}

export interface EntityConfigDto {
  ConsumerConfig: ConsumerConfigDto | null;
  ProviderConfig: ProviderConfigDto | null;
}

export interface EntityReportDto {
  EntityID: string;
  EntityType: 'consumer' | 'provider' | 'both' | 'unknown';
  ConsumerReports: ConsumerReportDto[];
  ProviderReports: ProviderReportDto[];
}

export interface CreateConsumerReportPayload {
  EntityID: string;
  EntryID?: string;
  ReportDate: string;
  EntryDescription?: string;
  EntryAmount: number;
  EntryCost: number;
}

export interface CreateProviderReportPayload {
  DataProviderID: string;
  EntryID?: string;
  ReportDate: string;
  EntryDescription?: string;
  Earnings: number;
}

@Injectable({
  providedIn: 'root'
})
export class ReportsApiService {
  private readonly apiUrl = this.resolveBaseUrl(environment.apiUrl);

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  getEntityConfig(): Observable<EntityConfigDto> {
    return this.post<EntityConfigDto>('/webapp/audience-report/get-config', {});
  }

  getEntityReport(payload: { entityID?: string; startDate?: string; endDate?: string; consumerEntityID?: string; Type?: 'provider' | 'consumer' }): Observable<EntityReportDto> {
    return this.post<EntityReportDto>('/webapp/audience-report/get-report', payload);
  }

  getProviderReports(filter: { startDate?: string; endDate?: string } = {}): Observable<ProviderReportDto[]> {
    return this.post<ProviderReportDto[]>('/webapp/audience-report/get-provider-reports', filter);
  }

  getConsumerReports(filter: { startDate?: string; endDate?: string } = {}): Observable<ConsumerReportDto[]> {
    return this.post<ConsumerReportDto[]>('/webapp/audience-report/get-consumer-reports', filter);
  }

  createConsumerReport(payload: CreateConsumerReportPayload): Observable<ConsumerReportDto> {
    return this.post<ConsumerReportDto>('/webapp/audience-report/create-consumer-report-row', payload);
  }

  updateConsumerReport(payload: UpdateConsumerReportPayload): Observable<{ status: string; message: string }> {
    return this.put<{ status: string; message: string }>('/webapp/audience-report/update-consumer-report-row', payload);
  }

  createProviderReport(payload: CreateProviderReportPayload): Observable<{ status: string; message: string }> {
    return this.post<{ status: string; message: string }>('/webapp/audience-report/create-provider-report-row', payload);
  }

  updateProviderReport(payload: UpdateProviderReportPayload): Observable<{ status: string; message: string }> {
    return this.put<{ status: string; message: string }>('/webapp/audience-report/update-provider-report-row', payload);
  }

  deleteConsumerReport(entryId: string): Observable<{ status: string; message: string }> {
    return this.delete<{ status: string; message: string }>('/webapp/audience-report/delete-consumer-report-row', { EntryID: entryId });
  }

  deleteProviderReport(entryId: string): Observable<{ status: string; message: string }> {
    return this.delete<{ status: string; message: string }>('/webapp/audience-report/delete-provider-report-row', { EntryID: entryId });
  }

  getAudienceNamesByIds(ids: string[]): Observable<{ ID: string; Name: string }[]> {
    return this.post<{ ID: string; Name: string }[]>('/webapp/audience/crud/names', { IDs: ids });
  }

  private post<T>(path: string, body: unknown): Observable<T> {
    return this.http.post<GenericResponse<T>>(
      `${this.apiUrl}${path}`,
      body,
      { headers: this.buildHeaders() }
    ).pipe(
      map(res => {
        if (res.error) {
          throw new Error(res.error.message || 'Request failed');
        }
        return res.payload as T;
      }),
      catchError(error => {
        console.error('[reports-api] request failed', path, error);
        return throwError(() => error);
      })
    );
  }

  private put<T>(path: string, body: unknown): Observable<T> {
    return this.http.put<GenericResponse<T>>(
      `${this.apiUrl}${path}`,
      body,
      { headers: this.buildHeaders() }
    ).pipe(
      map(res => {
        if (res.error) {
          throw new Error(res.error.message || 'Request failed');
        }
        return res.payload as T;
      }),
      catchError(error => {
        console.error('[reports-api] request failed', path, error);
        return throwError(() => error);
      })
    );
  }

  private delete<T>(path: string, body: unknown): Observable<T> {
    return this.http.request<GenericResponse<T>>('delete', `${this.apiUrl}${path}`, {
      body,
      headers: this.buildHeaders()
    }).pipe(
      map(res => {
        if (res.error) {
          throw new Error(res.error.message || 'Request failed');
        }
        return res.payload as T;
      }),
      catchError(error => {
        console.error('[reports-api] request failed', path, error);
        return throwError(() => error);
      })
    );
  }

  private buildHeaders(): HttpHeaders {
    let headers = new HttpHeaders(this.authService.getAuthHeaders());
    if (!headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }
    headers = headers.set('X-Product', 'vision');
    return headers;
  }

  private resolveBaseUrl(url: string): string {
    if (!url) {
      return '';
    }
    return url.replace(/\/api$/u, '').replace(/\/+$/u, '');
  }
}
